import { images } from '../constants';

const services = [
    {
        id: 1,
        image: images.service1,
        title: 'Event Registration',
        content: 'Register for events and purchase tickets through our platform.',
        paragraphs: [
            'Our event registration system simplifies the process of signing up for events. Whether you’re attending a business conference, a live concert, or a local workshop, you can secure your spot in just a few clicks.',
            'With a user-friendly interface, secure payment options, and instant confirmations, we ensure a hassle-free experience. Browse upcoming events, choose your preferred sessions, and complete your registration effortlessly.'
        ],
        gallery: [
            images.service1Gallery1,
            images.service1Gallery2
        ],
        features: {
            intro: 'Why choose our event registration platform?',
            list: [
                'Simple and fast online registration',
                'Secure payment options',
                'Instant confirmation and e-tickets',
                'Wide range of events to choose from',
                'User-friendly interface for seamless booking'
            ]
        },
        process: {
            intro: 'How to register for an event:',
            list: [
                'Browse available events on our platform',
                'Select your desired event and ticket type',
                'Fill in your details and confirm your booking',
                'Make a secure payment',
                'Receive your e-ticket instantly via email'
            ]
        },
        faqs: [
            {
                id: 1,
                heading: 'How do I register for an event?',
                content: 'You can register by selecting an event, choosing your ticket type, and completing the payment. You will receive a confirmation email with your e-ticket.'
            },
            {
                id: 2,
                heading: 'Can I purchase tickets for multiple events?',
                content: 'Yes, you can register for multiple events and manage your bookings from your account dashboard.'
            },
            {
                id: 3,
                heading: 'What payment methods do you accept?',
                content: 'We accept credit/debit cards, PayPal, and other secure online payment methods.'
            },
            {
                id: 4,
                heading: 'Can I cancel or transfer my ticket?',
                content: 'Ticket cancellation and transfer policies vary by event. Check the event details for specific terms before booking.'
            }
        ]
    },
    {
        id: 2,
        image: images.service2,
        title: 'Event Timetable',
        content: 'Access a comprehensive schedule of sessions and events.',
        paragraphs: [
            'Our event timetable provides a detailed schedule of all sessions, helping you plan your day efficiently. From keynote speeches to panel discussions and networking sessions, stay updated on what’s happening and when.',
            'With real-time updates and easy navigation, you can quickly find session details, speakers, and locations. Never miss an important event again!'
        ],
        gallery: [
            images.service2Gallery1,
            images.service2Gallery2
        ],
        features: {
            intro: 'Why use our event timetable?',
            list: [
                'Comprehensive schedule of all event sessions',
                'Easy-to-navigate timetable with filters',
                'Real-time updates on session changes',
                'Details on speakers, topics, and venues',
                'Personalized schedule management'
            ]
        },
        process: {
            intro: 'How to use the event timetable:',
            list: [
                'Browse the event schedule to view session timings',
                'Filter sessions by category, speaker, or venue',
                'Add sessions to your personal schedule',
                'Receive real-time updates on any schedule changes',
                'Stay organized and make the most of your event experience'
            ]
        },
        faqs: [
            {
                id: 1,
                heading: 'How can I view the full event schedule?',
                content: 'You can access the complete event timetable through our platform, where you’ll find details on all sessions, speakers, and venues.'
            },
            {
                id: 2,
                heading: 'Can I filter sessions by topic or speaker?',
                content: 'Yes! Our timetable allows you to filter sessions based on categories, speakers, and venues to easily find what interests you.'
            },
            {
                id: 3,
                heading: 'Will I get notified about schedule changes?',
                content: 'Yes, we provide real-time updates and notifications for any changes in the event schedule.'
            },
            {
                id: 4,
                heading: 'Can I create a personalized schedule?',
                content: 'Absolutely! You can bookmark sessions and create a personalized agenda to ensure you don’t miss important events.'
            }
        ]
    },
    {
        id: 3,
        image: images.service3,
        title: 'Networking',
        content: 'Connect with fellow attendees through networking sessions.',
        paragraphs: [
            'Our networking sessions provide a great opportunity to connect with industry leaders, experts, and like-minded professionals. Engage in meaningful conversations, share insights, and collaborate on new ideas.',
            'Whether you’re looking for potential business partners, job opportunities, or knowledge exchange, our networking events are designed to help you foster valuable connections.'
        ],
        gallery: [
            images.service3Gallery1,
            images.service3Gallery2
        ],
        features: {
            intro: 'Why join our networking sessions?',
            list: [
                'Meet and interact with industry professionals',
                'Engage in meaningful discussions and collaborations',
                'Expand your professional network',
                'Find potential business partners or career opportunities',
                'Participate in structured networking activities'
            ]
        },
        process: {
            intro: 'How to make the most of networking:',
            list: [
                'Register for networking sessions in advance',
                'Prepare a brief introduction about yourself and your interests',
                'Engage in conversations and exchange contact details',
                'Follow up with new connections after the event',
                'Leverage networking for future collaborations and opportunities'
            ]
        },
        faqs: [
            {
                id: 1,
                heading: 'How can I participate in networking sessions?',
                content: 'You can sign up for networking events through our platform and receive notifications about scheduled sessions.'
            },
            {
                id: 2,
                heading: 'Are networking sessions open to all attendees?',
                content: 'Yes, networking sessions are open to all registered attendees, and some events may have exclusive access for VIP participants.'
            },
            {
                id: 3,
                heading: 'Can I connect with attendees before the event?',
                content: 'Absolutely! Our platform allows you to view attendee profiles and connect before the event to schedule meetings.'
            },
            {
                id: 4,
                heading: 'Will there be structured networking activities?',
                content: 'Yes, we offer both structured networking activities and open mingling sessions to help attendees make valuable connections.'
            }
        ]
    },
    {
        id: 4,
        image: images.service4,
        title: 'Speaker Profiles',
        content: 'Explore profiles of speakers for a better experience.',
        paragraphs: [
            'Our speaker profiles provide in-depth information about the experts presenting at the event. Learn about their background, achievements, and areas of expertise before attending their sessions.',
            'Gain valuable insights by exploring their previous work, key contributions to the industry, and what to expect from their talks. This helps you make the most of your event experience.'
        ],
        gallery: [
            images.service4Gallery1,
            images.service4Gallery2
        ],
        features: {
            intro: 'Why explore speaker profiles?',
            list: [
                'Get insights into the expertise of industry leaders',
                'Learn about speakers’ backgrounds and achievements',
                'Discover the topics they will be covering',
                'Plan your schedule based on speaker sessions',
                'Engage with speakers through Q&A sessions'
            ]
        },
        process: {
            intro: 'How to use speaker profiles effectively:',
            list: [
                'Browse the list of speakers on our platform',
                'Read about their professional journey and achievements',
                'Check their scheduled sessions and topics',
                'Prepare questions for interactive Q&A opportunities',
                'Connect with speakers through networking events'
            ]
        },
        faqs: [
            {
                id: 1,
                heading: 'Where can I find speaker profiles?',
                content: 'Speaker profiles are available on our event platform under the "Speakers" section, where you can browse detailed information.'
            },
            {
                id: 2,
                heading: 'Can I interact with speakers?',
                content: 'Yes, you can attend their sessions, participate in Q&A discussions, and connect with them through networking events.'
            },
            {
                id: 3,
                heading: 'How do I know which sessions a speaker is presenting?',
                content: 'Each speaker profile includes details about their scheduled sessions, including topics, time, and venue.'
            },
            {
                id: 4,
                heading: 'Can I follow up with speakers after the event?',
                content: 'Yes, many speakers share their contact details or professional links, allowing you to stay connected even after the event.'
            }
        ]
    },
    {
        id: 5,
        image: images.service5,
        title: 'Virtual Attendance',
        content: 'Join events virtually with live streaming.',
        paragraphs: [
            'Our virtual attendance feature allows you to participate in events remotely through high-quality live streaming. Engage with speakers, access session materials, and interact with fellow attendees—all from the comfort of your home or office.',
            'Whether you’re unable to attend in person or prefer online participation, our platform ensures a seamless and immersive event experience with interactive features like live Q&A and chat support.'
        ],
        gallery: [
            images.service5Gallery1,
            images.service5Gallery2
        ],
        features: {
            intro: 'Why choose virtual attendance?',
            list: [
                'Live streaming of all key sessions',
                'Interactive Q&A with speakers',
                'Access to session recordings',
                'Join from anywhere with an internet connection',
                'Seamless virtual networking opportunities'
            ]
        },
        process: {
            intro: 'How to join virtually:',
            list: [
                'Register for virtual access on our platform',
                'Receive a unique event link via email',
                'Log in on the event day to access live streams',
                'Engage with speakers through live Q&A',
                'Watch recorded sessions at your convenience'
            ]
        },
        faqs: [
            {
                id: 1,
                heading: 'How do I register for virtual attendance?',
                content: 'You can register for virtual attendance through our platform by selecting the "Virtual Ticket" option during registration.'
            },
            {
                id: 2,
                heading: 'Can I interact with speakers during virtual sessions?',
                content: 'Yes! Our live streaming includes interactive Q&A features, allowing you to ask questions and engage with speakers in real time.'
            },
            {
                id: 3,
                heading: 'Will session recordings be available?',
                content: 'Yes, all live sessions will be recorded and available for registered attendees to watch later.'
            },
            {
                id: 4,
                heading: 'What do I need to attend virtually?',
                content: 'You only need a stable internet connection and a device (laptop, tablet, or smartphone) to access the event.'
            }
        ]
    },
    {
        id: 6,
        image: images.service6,
        title: 'Event Resources',
        content: 'Access presentations, and resources to enhance your learning.',
        paragraphs: [
            'Our platform provides attendees with valuable event resources, including session presentations, whitepapers, and supplementary materials. These resources help reinforce key insights and provide deeper learning beyond the live sessions.',
            'Whether you want to revisit a keynote presentation, download speaker slides, or access exclusive reports, our resource center ensures you have everything you need to maximize your event experience.'
        ],
        gallery: [
            images.service6Gallery1,
            images.service6Gallery2
        ],
        features: {
            intro: 'Why use event resources?',
            list: [
                'Access exclusive session presentations',
                'Download whitepapers, reports, and guides',
                'Revisit keynotes and expert insights',
                'Enhance learning with additional materials',
                'Stay updated with event-related documents'
            ]
        },
        process: {
            intro: 'How to access event resources:',
            list: [
                'Register and log in to the event platform',
                'Navigate to the "Resources" section',
                'Browse available session materials',
                'Download presentations and reports',
                'Access recordings for on-demand learning'
            ]
        },
        faqs: [
            {
                id: 1,
                heading: 'How do I access event materials?',
                content: 'Registered attendees can log in to our platform and navigate to the "Resources" section to download presentations and reports.'
            },
            {
                id: 2,
                heading: 'Are session recordings available?',
                content: 'Yes, recordings of key sessions will be available for attendees to watch on demand after the event.'
            },
            {
                id: 3,
                heading: 'Can I share downloaded resources?',
                content: 'Event materials are for personal use only. Please check with the event organizers before sharing any resources.'
            },
            {
                id: 4,
                heading: 'Do I need to pay extra for resource access?',
                content: 'Most resources are included with your event registration, but some premium materials may require an additional purchase.'
            }
        ]
    }
];

export default services;